import React from 'react'
import { SparklesIcon } from '@heroicons/react/24/outline'

// 基础骨架屏 - 增强版
export const Skeleton = ({
  className = '',
  animate = true
}: {
  className?: string
  animate?: boolean
}) => (
  <div
    className={`bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 rounded-lg ${
      animate ? 'animate-shimmer bg-[length:200%_100%]' : ''
    } ${className}`}
  />
)

// 品牌加载器 - 优化版
export const LoadingSpinner = ({
  size = 'md',
  className = '',
  variant = 'default'
}: {
  size?: 'sm' | 'md' | 'lg'
  className?: string
  variant?: 'default' | 'gradient'
}) => {
  const sizeClasses = {
    sm: 'w-5 h-5 border-2',
    md: 'w-8 h-8 border-[3px]',
    lg: 'w-12 h-12 border-4'
  }

  if (variant === 'gradient') {
    return (
      <div className="relative flex items-center justify-center">
        <div
          className={`animate-spin rounded-full ${sizeClasses[size]} ${className}`}
          style={{
            background: 'conic-gradient(from 0deg, transparent, #6366F1)',
            mask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 2px))',
            WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 2px))'
          }}
        />
      </div>
    )
  }

  return (
    <div className="relative flex items-center justify-center">
      <div
        className={`animate-spin rounded-full border-slate-200 border-t-indigo-600 ${sizeClasses[size]} ${className}`}
      />
    </div>
  )
}

// 智能加载卡片 - 增强版
export const SkeletonCard = ({ className = '' }: { className?: string }) => {
  return (
    <div
      className={`bg-white rounded-2xl border border-slate-100 p-6 shadow-sm overflow-hidden ${className}`}
    >
      {/* 顶部渐变装饰 */}
      <div className="h-1 w-full bg-gradient-to-r from-indigo-200 via-purple-200 to-pink-200 -mx-6 -mt-6 mb-6" />

      <div className="flex items-center space-x-4 mb-6">
        <Skeleton className="w-14 h-14 rounded-2xl" />
        <div className="flex-1 space-y-2.5">
          <Skeleton className="h-4 w-2/5 rounded-lg" />
          <Skeleton className="h-3 w-1/4 rounded-lg" />
        </div>
      </div>

      <div className="space-y-3">
        <Skeleton className="h-4 w-full rounded-lg" />
        <Skeleton className="h-4 w-5/6 rounded-lg" />
        <Skeleton className="h-4 w-4/6 rounded-lg" />
      </div>

      <div className="mt-6 flex justify-between items-center pt-4 border-t border-slate-50">
        <Skeleton className="h-10 w-28 rounded-xl" />
        <Skeleton className="h-10 w-10 rounded-xl" />
      </div>
    </div>
  )
}

// 全屏品牌加载页 - 豪华版
export const FullScreenLoading = ({
  message = '正在准备您的学习之旅...',
  submessage
}: {
  message?: string
  submessage?: string
}) => {
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 backdrop-blur-sm flex flex-col items-center justify-center z-50">
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-indigo-200/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative z-10 flex flex-col items-center">
        {/* Logo 动画 */}
        <div className="relative mb-10">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl blur-2xl opacity-30 animate-pulse" />
          <div className="relative bg-white p-5 rounded-3xl shadow-2xl border border-slate-100 animate-float">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <SparklesIcon className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        {/* 文字 */}
        <h3 className="text-xl font-bold text-slate-800 mb-2">{message}</h3>
        {submessage && <p className="text-sm text-slate-500 mb-6">{submessage}</p>}

        {/* 进度条 */}
        <div className="w-64 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 w-1/3 animate-[shimmer_1.5s_ease-in-out_infinite] bg-[length:200%_100%]" />
        </div>
      </div>
    </div>
  )
}

// 文本骨架屏 - 增强版
export const SkeletonText = ({
  lines = 3,
  className = '',
  gap = 'default'
}: {
  lines?: number
  className?: string
  gap?: 'tight' | 'default' | 'loose'
}) => {
  const gapClasses = {
    tight: 'space-y-1.5',
    default: 'space-y-2.5',
    loose: 'space-y-3.5'
  }

  return (
    <div className={`${gapClasses[gap]} ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={`h-4 rounded-lg ${i === lines - 1 ? 'w-3/5' : i === lines - 2 ? 'w-4/5' : 'w-full'}`}
        />
      ))}
    </div>
  )
}

// 数据加载状态 - 增强版
export const DataLoading = ({
  message = '数据加载中...',
  size = 'md'
}: {
  message?: string
  size?: 'sm' | 'md' | 'lg'
}) => {
  const containerClasses = {
    sm: 'py-6',
    md: 'py-12',
    lg: 'py-20'
  }

  return (
    <div
      className={`flex flex-col items-center justify-center text-slate-500 space-y-4 ${containerClasses[size]}`}
    >
      <div className="relative">
        <div className="absolute inset-0 bg-indigo-500/20 rounded-full blur-lg animate-pulse" />
        <LoadingSpinner size={size} variant="gradient" />
      </div>
      <p className="text-sm font-medium text-slate-600">{message}</p>
    </div>
  )
}

// 列表骨架屏
export const SkeletonList = ({
  count = 3,
  className = ''
}: {
  count?: number
  className?: string
}) => (
  <div className={`space-y-4 ${className}`}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-slate-100">
        <Skeleton className="w-12 h-12 rounded-xl" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/3 rounded-lg" />
          <Skeleton className="h-3 w-2/3 rounded-lg" />
        </div>
        <Skeleton className="w-20 h-8 rounded-lg" />
      </div>
    ))}
  </div>
)

// 表格骨架屏
export const SkeletonTable = ({
  rows = 5,
  cols = 4,
  className = ''
}: {
  rows?: number
  cols?: number
  className?: string
}) => (
  <div className={`bg-white rounded-2xl border border-slate-100 overflow-hidden ${className}`}>
    {/* 表头 */}
    <div className="flex items-center gap-4 p-4 bg-slate-50 border-b border-slate-100">
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} className="h-4 flex-1 rounded-lg" />
      ))}
    </div>
    {/* 表体 */}
    {Array.from({ length: rows }).map((_, row) => (
      <div key={row} className="flex items-center gap-4 p-4 border-b border-slate-50 last:border-0">
        {Array.from({ length: cols }).map((_, col) => (
          <Skeleton key={col} className="h-4 flex-1 rounded-lg" />
        ))}
      </div>
    ))}
  </div>
)

// 页面加载状态
export const PageLoading = ({ title = '加载中' }: { title?: string }) => (
  <div className="min-h-[60vh] flex flex-col items-center justify-center">
    <div className="relative mb-6">
      <div className="w-20 h-20 rounded-full border-4 border-indigo-100 border-t-indigo-500 animate-spin" />
      <div className="absolute inset-0 flex items-center justify-center">
        <SparklesIcon className="w-8 h-8 text-indigo-500" />
      </div>
    </div>
    <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
    <p className="text-sm text-slate-500 mt-1">请稍候片刻...</p>
  </div>
)

export default {
  Skeleton,
  SkeletonCard,
  SkeletonText,
  SkeletonList,
  SkeletonTable,
  LoadingSpinner,
  FullScreenLoading,
  DataLoading,
  PageLoading
}
